import { Grid, InputAdornment } from "@mui/material";
import Box from "@mui/material/Box";
import * as yup from "yup";
import { useFormik } from "formik";
import { useEffect } from "react";
import { NumericFormatCustom } from "@stories/atoms/NumericFormatCustom/NumericFormatCustom";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { calcRequiredDeemedHours, calculatedAvailableSpend } from "@utils/calc";

interface PackageMPSGFormProps {
  contractorPackage: ContractorPackageLineItem;
  handleSubmit: (contractorPackage: ContractorPackageLineItem) => void;
  handleBack: () => void;
}

export const PackageMPSGForm = ({
  contractorPackage,
  handleSubmit,
  handleBack,
}: PackageMPSGFormProps) => {
  const validationSchema = yup.object({
    totalPackageValue: yup.number().min(0).required(),
    packageMargin: yup
      .number()
      .min(0)
      .required()
      .test(
        "Less than package total",
        "Margin should be less than total package value",
        function (item) {
          return this.parent.totalPackageValue > item ? true : false;
        }
      ),
    specialisedEquipmentValue: yup.number().min(0),
    adjustedMpsgValue: yup.number().min(0),
    deemedLabourRatio: yup.number().min(0),
    deemedLabourHourlyRate: yup.number().min(0),
    mpsgHoursRequirement: yup.number().min(0),
  });

  const formik = useFormik({
    initialValues: {
      totalPackageValue: contractorPackage.totalPackageValue,
      packageMargin: contractorPackage.packageMargin,
      variations: contractorPackage.variations,
      specialisedEquipmentValue: contractorPackage.specialisedEquipmentValue,
      adjustedMpsgValue: calculatedAvailableSpend(
        contractorPackage.totalPackageValue,
        contractorPackage.packageMargin,
        0,
        contractorPackage.specialisedEquipmentValue
      ),
      requiredHours: calcRequiredDeemedHours(
        calculatedAvailableSpend(
          contractorPackage.totalPackageValue,
          contractorPackage.packageMargin,
          0,
          contractorPackage.specialisedEquipmentValue
        ),
        contractorPackage.deemedLabourRatio,
        contractorPackage.deemedLabourHourlyRate
      ),
      deemedLabourRatio: contractorPackage.deemedLabourRatio,
      deemedLabourHourlyRate: contractorPackage.deemedLabourHourlyRate,
      requiredSpend:
        contractorPackage.totalPackageValue - contractorPackage.packageMargin,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      handleSubmit({
        ...contractorPackage,
        totalPackageValue: values.totalPackageValue,
        packageMargin: values.packageMargin,
        specialisedEquipmentValue: values.specialisedEquipmentValue,
        deemedLabourRatio: values.deemedLabourRatio,
        deemedLabourHourlyRate: values.deemedLabourHourlyRate,
        requiredHours: values.requiredHours,
      });
    },
  });

  useEffect(() => {
    if (
      formik.values.totalPackageValue > 0 &&
      formik.values.packageMargin > 0
    ) {
      const adjustedMpsgValue = calculatedAvailableSpend(
        formik.values.totalPackageValue,
        formik.values.packageMargin,
        formik.values.variations,
        formik.values.specialisedEquipmentValue
      );
      formik.setFieldValue("adjustedMpsgValue", adjustedMpsgValue.toFixed(2));
    }
  }, [
    formik.values.totalPackageValue,
    formik.values.packageMargin,
    formik.values.specialisedEquipmentValue,
  ]);

  useEffect(() => {
    if (
      formik.values.adjustedMpsgValue > 0 &&
      formik.values.deemedLabourRatio > 0 &&
      formik.values.deemedLabourHourlyRate > 0
    ) {
      const requiredHours =
        (formik.values.adjustedMpsgValue * formik.values.deemedLabourRatio) /
        100 /
        formik.values.deemedLabourHourlyRate;
      formik.setFieldValue("requiredHours", requiredHours.toFixed(2));
    }
  }, [
    formik.values.adjustedMpsgValue,
    formik.values.deemedLabourRatio,
    formik.values.deemedLabourHourlyRate,
  ]);

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={2} sm={2}>
              <StyledTextField
                required
                id="totalPackageValue"
                name="totalPackageValue"
                label="Total Package Value (ex. GST)"
                fullWidth
                margin="dense"
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.totalPackageValue}
                error={
                  formik.touched.totalPackageValue &&
                  Boolean(formik.errors.totalPackageValue)
                }
                helperText={
                  formik.touched.totalPackageValue
                    ? formik.errors.totalPackageValue
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={2} sm={2}>
              <StyledTextField
                required
                id="packageMargin"
                name="packageMargin"
                label="Margin"
                fullWidth
                margin="dense"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.packageMargin}
                error={
                  formik.touched.packageMargin &&
                  Boolean(formik.errors.packageMargin)
                }
                helperText={
                  formik.touched.packageMargin
                    ? formik.errors.packageMargin
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={2} sm={2}>
              <StyledTextField
                id="specialistEquipment"
                name="specialistEquipment"
                label="Less: Specialist Equipment"
                fullWidth
                margin="dense"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.specialisedEquipmentValue}
                error={
                  formik.touched.specialisedEquipmentValue &&
                  Boolean(formik.errors.specialisedEquipmentValue)
                }
                helperText={
                  formik.touched.specialisedEquipmentValue
                    ? formik.errors.specialisedEquipmentValue
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <StyledTextField
                id="adjustedMpsgValue"
                name="adjustedMpsgValue"
                label="Less Margin"
                fullWidth
                margin="dense"
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.adjustedMpsgValue}
                error={
                  formik.touched.adjustedMpsgValue &&
                  Boolean(formik.errors.adjustedMpsgValue)
                }
                helperText={
                  formik.touched.adjustedMpsgValue
                    ? formik.errors.adjustedMpsgValue
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={6}>
              <StyledTextField
                required
                id="deemedLabourRatio"
                name="deemedLabourRatio"
                label="Deemed Labour Ratio"
                fullWidth
                margin="dense"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.deemedLabourRatio}
                error={
                  formik.touched.deemedLabourRatio &&
                  Boolean(formik.errors.deemedLabourRatio)
                }
                helperText={
                  formik.touched.deemedLabourRatio
                    ? formik.errors.deemedLabourRatio
                    : ""
                }
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <StyledTextField
                id="requiredSpend"
                name="requiredSpend"
                label="Required Spend"
                fullWidth
                margin="dense"
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.requiredSpend}
                error={
                  formik.touched.requiredSpend &&
                  Boolean(formik.errors.requiredSpend)
                }
                helperText={
                  formik.touched.requiredSpend
                    ? formik.errors.requiredSpend
                    : ""
                }
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">hours</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={6}>
              <StyledTextField
                required
                id="deemedLabourHourlyRate"
                name="deemedLabourHourlyRate"
                label="Deemed Hourly Rate"
                fullWidth
                margin="dense"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.deemedLabourHourlyRate}
                error={
                  formik.touched.deemedLabourHourlyRate &&
                  Boolean(formik.errors.deemedLabourHourlyRate)
                }
                helperText={
                  formik.touched.deemedLabourHourlyRate
                    ? formik.errors.deemedLabourHourlyRate
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <StyledTextField
                id="requiredHours"
                name="requiredHours"
                label="Required Hours"
                fullWidth
                margin="dense"
                disabled={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.requiredHours}
                error={
                  formik.touched.requiredHours &&
                  Boolean(formik.errors.requiredHours)
                }
                helperText={
                  formik.touched.requiredHours
                    ? formik.errors.requiredHours
                    : ""
                }
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">hours</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container mt={1}>
            <Grid
              item
              xs={6}
              sm={6}
              sx={{
                display: "flex !important",
                justifyContent: "left !important;",
              }}
            >
              <StyledButton variant="contained" onClick={handleBack}>
                Back
              </StyledButton>
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              sx={{
                display: "flex !important",
                justifyContent: "right !important;",
              }}
            >
              <StyledButton variant="contained" type="submit">
                Next
              </StyledButton>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
};
